import { useState, useEffect, useMemo } from "react";
import {
  Table,
  Input,
  Select,
  Button,
  Space,
  Tag,
  Modal,
  Form,
  Row,
  Col,
  Drawer,
  notification,
  message,
  Checkbox,
} from "antd";
import {
  getCTAList,
  getCTALog,
  getAllBrands,
  updateCTAStatus,
} from "../services/ctaService.js";
import EllipsisText from "./EllipsisText";
import dayjs from 'dayjs';
import styles from "./CTAList.module.css";
import { addDateRange } from "../utils/common.js";
import CTADetail from "./CTADetail";
import StatusModal from './StatusModal';
import DateRangeSelector from './dashboard/DateRangeSelector.jsx';
import { download } from '../utils/download';
import { CopyOutlined, HolderOutlined, SettingOutlined } from '@ant-design/icons';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import logIcon from '../assets/log.svg';
import { getCompanyList } from "../services/userService.js";
import { useGlobalState } from '../hooks/useGlobalState.js'
import CtaTicketStatusTag from './common/CtaTicketStatusTag.jsx';
import CtaTicketCreateModal from './common/CtaTicketCreateModal.jsx';
import { DndContext } from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const CTA_COLUMN_SETTINGS_STORAGE_KEY = "cta-list-column-settings";

const cloneColumnConfigs = (configs = []) => configs.map((config) => ({ ...config }));

const loadColumnSettings = (storageKey) => {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    const rawValue = window.localStorage.getItem(storageKey);
    return rawValue ? JSON.parse(rawValue) : null;
  } catch (error) {
    console.error("Failed to load CTA column settings:", error);
    return null;
  }
};

const persistColumnSettings = (storageKey, configs) => {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.localStorage.setItem(storageKey, JSON.stringify(configs));
  } catch (error) {
    console.error("Failed to persist CTA column settings:", error);
  }
};

const clearColumnSettings = (storageKey) => {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.localStorage.removeItem(storageKey);
  } catch (error) {
    console.error("Failed to clear CTA column settings:", error);
  }
};

const mergeColumnConfigs = (defaultConfigs = [], savedConfigs = []) => {
  if (!Array.isArray(savedConfigs) || savedConfigs.length === 0) {
    return cloneColumnConfigs(defaultConfigs);
  }

  const defaultConfigMap = new Map(
    defaultConfigs.map((config) => [config.key, { ...config }])
  );
  const mergedConfigs = [];

  savedConfigs.forEach((savedConfig) => {
    if (!savedConfig?.key || !defaultConfigMap.has(savedConfig.key)) {
      return;
    }

    const defaultConfig = defaultConfigMap.get(savedConfig.key);
    mergedConfigs.push({
      ...defaultConfig,
      visible:
        typeof savedConfig.visible === "boolean"
          ? savedConfig.visible
          : defaultConfig.visible,
    });
    defaultConfigMap.delete(savedConfig.key);
  });

  defaultConfigs.forEach((defaultConfig) => {
    if (defaultConfigMap.has(defaultConfig.key)) {
      mergedConfigs.push({ ...defaultConfig });
    }
  });

  return mergedConfigs;
};

const moveColumnsToFront = (configs = [], columnKeys = []) => {
  const keySet = new Set(columnKeys);
  const pinnedColumns = columnKeys
    .map((key) => configs.find((config) => config.key === key))
    .filter(Boolean);
  const remainingColumns = configs.filter((config) => !keySet.has(config.key));

  return [...pinnedColumns, ...remainingColumns];
};

const SortableColumnSettingItem = ({ column, onToggle }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: column.key });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`${styles.columnSettingsItem} ${isDragging ? styles.columnSettingsItemDragging : ""}`}
    >
      <Checkbox
        checked={column.visible}
        onChange={(event) => onToggle(column.key, event.target.checked)}
        className={styles.columnSettingCheckbox}
      >
        <span className={styles.columnSettingLabel}>
          {column.key === "isWhitelist" ? "Trust List Status" : column.title}
        </span>
      </Checkbox>
      <Button
        type="text"
        size="small"
        icon={<HolderOutlined />}
        ref={setActivatorNodeRef}
        {...attributes}
        {...listeners}
        className={styles.columnDragHandle}
      />
    </div>
  );
};

const CTAList = () => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [selectedRows, setSelectedRows] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [modalForm] = Form.useForm();
  const [filters, setFilters] = useState(location.state?.filters || {});
  const [pagination, setPagination] = useState({
    current: location.state?.pageInfo?.current || 1,
    pageSize: location.state?.pageInfo?.pageSize || 10
  });
  const [logPagination, setLogPagination] = useState({
    current: 1,
    pageSize: 10
  });
  const { userProfile } = useGlobalState();
  const [data, setData] = useState({ rows: [], total: 0 });
  const [logData, setLogData] = useState({ rows: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const [brands, setBrands] = useState([]);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [currentRecord, setCurrentRecord] = useState(null);
  const [sorter, setSorter] = useState({ orderByColumn: 'lastQueryTime', isAsc: 'desc' });
  const [previousRoute, setPreviousRoute] = useState(location.state?.from);
  const { TextArea } = Input;
  const [isLogCenterOpen, setIsLogCenterOpen] = useState(false);
  const [isColumnSettingsOpen, setIsColumnSettingsOpen] = useState(false);
  const [savedColumnConfig, setSavedColumnConfig] = useState([]);
  const [draftColumnConfig, setDraftColumnConfig] = useState([]);
  const navigate = useNavigate();
  const { Option } = Select;
  const statusOptions = [
    { value: "Submitted", label: "Submitted" },
    { value: "Under Review", label: "Under Review" },
    { value: "Disruption In Progress", label: "Disruption In Progress" },
    { value: "Verified Scam", label: "Verified Scam" },
    { value: "Verified Genuine", label: "Verified Genuine" },
    { value: "Notify Brand", label: "Notify Brand" },
    { value: "Scam Disrupted", label: "Scam Disrupted" },
    // { value: "Closed", label: "Closed" },
  ];
  const hiddenColumnsForCompanyUser = [
    "companyName"
  ];
  const [companys, setCompanys] = useState([]);
  const [ticketModalVisible, setTicketModalVisible] = useState(false);
  const [eligibleRecords, setEligibleRecords] = useState([]);
  const MAX_TICKET_COUNT = 5;
  const ELIGIBLE_TYPES = ['URL', 'Phone'];
  const isCompanyUser = userProfile?.user?.userStatus === "Company User";
  const columnSettingsStorageKey = `${CTA_COLUMN_SETTINGS_STORAGE_KEY}:${userProfile?.user?.userId ?? "guest"}:${userProfile?.user?.userStatus ?? "default"}`;
  const defaultColumnConfig = useMemo(() => ([
    { key: "ctaId", title: "CTA ID", visible: true },
    { key: "finalCta", title: "Standard CTA Format", visible: true },
    { key: "cta", title: "Original CTA Content", visible: true },
    { key: "isWhitelist", title: "Trust List Status", visible: true },
    { key: "ctaType", title: "CTA Type", visible: true },
    { key: "companyName", title: "Company", visible: true },
    { key: "relatedBrands", title: "Related Brands", visible: true },
    { key: "ctaStatus", title: "Status", visible: true },
    { key: "srNames", title: "Report Number", visible: true },
    { key: "conversationCount", title: "Associated Conversation Count", visible: true },
    { key: "isReport", title: "Reported", visible: true },
    { key: "riskLevel", title: "Risk Level", visible: true },
    { key: "firstTime", title: "First Detected Time", visible: true },
    { key: "lastQueryTime", title: "Last Updated Time", visible: true },
    { key: "action", title: "Actions", visible: true },
  ].filter((config) => !(isCompanyUser && hiddenColumnsForCompanyUser.includes(config.key)))), [isCompanyUser]);

  useEffect(() => {
    fetchCTAList();
    fetchAllBrands();

  }, [sorter]);

  useEffect(() => {
    const mergedColumnConfig = mergeColumnConfigs(
      defaultColumnConfig,
      loadColumnSettings(columnSettingsStorageKey)
    );
    const orderedColumnConfig = isCompanyUser
      ? moveColumnsToFront(mergedColumnConfig, ["ctaId"])
      : mergedColumnConfig;
    setSavedColumnConfig(orderedColumnConfig);
    setDraftColumnConfig(orderedColumnConfig);
  }, [columnSettingsStorageKey, defaultColumnConfig, isCompanyUser]);

  useEffect(() => {
    if (previousRoute != undefined) {
      handleView(previousRoute)
    }
    getCompany();
  }, []);

  useEffect(() => {
    if (location.state?.filters && Object.keys(location.state.filters).length > 0) {
      if ((location.state.filters.lastQueryTimeStart || location.state.filters.lastQueryTimeEnd) && !location.state.filters.dateRange) {
        const tempFilters = { ...location.state.filters };
        tempFilters.dateRange = [
          location.state.filters.lastQueryTimeStart ? dayjs(location.state.filters.lastQueryTimeStart) : null,
          location.state.filters.lastQueryTimeEnd ? dayjs(location.state.filters.lastQueryTimeEnd) : null
        ].filter(Boolean);
        form.setFieldsValue(tempFilters);
      } else {
        form.setFieldsValue(location.state.filters);
      }
    }
    if (location.state?.needReopenDrawer && location.state?.currentRecord) {
      setCurrentRecord(location.state.currentRecord);
      setDrawerVisible(true);
      navigate(location.pathname, {
        replace: true,
        state: {
          ...location.state,
          needReopenDrawer: false
        }
      });
    }
  }, [location.state]);

  useEffect(() => {
    const searchCta = searchParams.get('searchCta');
    const autoSearch = searchParams.get('autoSearch');
    if (autoSearch === 'true' && searchCta) {
      setTimeout(() => {
        const searchConditions = { cta: searchCta };
        form.setFieldsValue(searchConditions);
        handleSearch(searchConditions);
        setFilters(searchConditions);
        fetchCTAList(searchConditions, { current: 1, pageSize: pagination.pageSize }, sorter);
      }, 100);
    }
  }, [searchParams]);

  const processCTAInput = (input) => {
    if (!input || typeof input !== 'string') return [];

    const trimmedInput = input.trim();
    if (trimmedInput.length === 0) return [];
    if (trimmedInput.includes('\n')) {
      return trimmedInput.split('\n')
        .map(item => item.trim())
        .filter(item => item.length > 0);
    }
    if (trimmedInput.startsWith('http://') || trimmedInput.startsWith('https://')) {
      return [trimmedInput];
    }
    if (trimmedInput.includes(',')) {
      return trimmedInput.split(',')
        .map(item => item.trim())
        .filter(item => item.length > 0);
    }
    return [trimmedInput];
  };

  const handleBatchUpdate = () => {
    if (selectedRows.length === 0) {
      Modal.warning({
        title: "Please select records to update",
        content: "You need to select CTA records to batch update first.",
      });
      return;
    }
    setIsModalVisible(true);
  };

  const handleCreateTicket = () => {
    if (selectedRows.length === 0) {
      return;
    }

    const eligible = selectedRows.filter(
      (row) =>
        ELIGIBLE_TYPES.includes(row.ctaType) && !row.workOrderId
    );
    const ineligibleCount = selectedRows.length - eligible.length;

    if (eligible.length === 0) {
      message.warning(
        <div style={{ textAlign: 'left' }}>
          <div>None of the selected CTAs are eligible for ticket creation.</div>
          <div>Currently, only URL and Phone CTAs support ticket creation.</div>
        </div>
      );
      return;
    }

    if (eligible.length > MAX_TICKET_COUNT) {
      message.warning(
        <div style={{ textAlign: 'left' }}>
          <div>You can create tickets for up to {MAX_TICKET_COUNT} CTAs at a time.</div>
          <div>Currently, only URL and Phone CTAs support ticket creation.</div>
        </div>
      );
      return;
    }

    if (ineligibleCount > 0) {
      message.info(
        <div style={{ textAlign: 'left' }}>
          <div>Some selected CTAs are not eligible for ticket creation and have been automatically excluded.</div>
          <div>Currently, only URL and Phone CTAs support ticket creation.</div>
        </div>
      );
    }

    setEligibleRecords(eligible);
    setTicketModalVisible(true);
  };

  const handleTicketSuccess = () => {
    setSelectedRows([]);
    setSelectedRowKeys([]);
    setTicketModalVisible(false);
    fetchCTAList();
  };
  const resetData = () => {
    form.resetFields();
    setFilters({})
    setSorter({ orderByColumn: '', isAsc: '' });
    navigate('/cta', {
      state: {
        current: 1,
        pageSize: pagination.pageSize,
      }
    });
  };
  const closeDrawer = () => {
    setIsLogCenterOpen(false)
    setLogPagination({ current: 1, pageSize: 10 });
  }
  const handleExport = async () => {
    if (selectedRows.length === 0) {
      notification.warning({
        message: "Please select the records you want to export.",
        description: "You need to select the CTA record you want to export first.",
      });
      return;
    }

    if (loading) return;
    setLoading(true);

    const encodeBase64 = (message) => btoa(unescape(encodeURIComponent(message)));
    const selectedCtaIds = selectedRows.map(row => row.ctaId);
    const selectedCtaNames = selectedRows.map((row) => encodeBase64(row.cta))
    try {
      await download(
        '/cta/exportCtaList',
        //{ ids: selectedCtaIds },
        { ids: selectedCtaIds, ctaNames: selectedCtaNames },
        `CTA_export_${dayjs().format('YYYYMMDDHHmmss')}.xlsx`
      );
      setSelectedRows([]);
      setSelectedRowKeys([]);
    } catch (error) {
      console.error('Export error:', error);
    } finally {
      setLoading(false);
    }
  };


  const fetchLog = async (pagination) => {
    try {
      setIsLogCenterOpen(true);
      const response = await getCTALog({
        pageNum: pagination.current,
        pageSize: pagination.pageSize,
        companyId: userProfile?.user?.companyId,
      });
      setLogData({
        rows: response.rows || [],
        total: response.total || 0,
      });
    } catch (error) {
      console.error('Fetch log error:', error);
    }
  }

  const handleModalOk = async () => {
    if (loading) return;
    setLoading(true);
    try {
      const values = await modalForm.validateFields();
      const ctaIds = selectedRows.map((row) => row.externalRef);
      const ctaIdList = selectedRows.map((row) => row.ctaId);

      const valueStatus = values.status;
      const valueIsWhitelist = values.isWhitelist;

      if (valueStatus === undefined && valueIsWhitelist === undefined) {
        Modal.warning({
          title: "Please select a status to update",
          content: "You need to select a status before updating.",
        });
        return;
      }

      const ctaWhitelist = selectedRows.map((row) => row.isWhitelist == valueIsWhitelist);
      if (valueIsWhitelist !== undefined && ctaWhitelist.some(val => val === true)) {
        Modal.warning({
          content: "Some whitelist statuses are the same as the original. Please make a new selection.",
        });
        return;
      }
      const encodeBase64 = (message) => btoa(unescape(encodeURIComponent(message)));
      await updateCTAStatus({
        ctas: ctaIds,
        status: valueStatus,
        isWhitelist: valueIsWhitelist,
        ctaIds: ctaIdList,
        cta: selectedRows.map((row) => encodeBase64(row.cta)),
      });

      setIsModalVisible(false);
      modalForm.resetFields();
      fetchCTAList();
      setSelectedRows([]);
      setSelectedRowKeys([]);
    } catch (error) {
      console.error('Validation failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllBrands = async () => {
    const companyId = userProfile?.user?.companyId ?? null;

    const response = await getAllBrands(companyId);
    const brandsMap = response.map((brand) => ({ value: brand, label: brand }));
    setBrands(brandsMap);
  };

  const getCompany = async () => {
    try {
      const response = await getCompanyList();
      setCompanys(response.rows);
    } catch (error) {
      console.error("Fail to fetch company list:", error);
    }
  }

  const encodeBase64 = (message) => btoa(unescape(encodeURIComponent(message)));

  const fetchCTAList = async (params = filters, newPagination = pagination, newSorter = sorter) => {
    setLoading(true);
    const companyId = params.companyId ?? userProfile?.user?.companyId ?? null;
    try {
      const response = await getCTAList({
        ...params,
        cta: params.cta ? encodeBase64(params.cta) : undefined,
        ctas: params.ctaList ? encodeBase64(params.ctaList) : undefined,
        ctaList: undefined,
        pageNum: newPagination.current,
        pageSize: newPagination.pageSize,
        orderByColumn: newSorter.orderByColumn,
        isAsc: newSorter.isAsc,
        companyId: companyId,
      });
      if (response?.code === 200) {
        const rows = response.rows.map((item, index) => ({
          key: (newPagination.current - 1) * newPagination.pageSize + index + 1,
          ...item,
        }));
        setData({ rows, total: response.total });
      }
    } catch (error) {
      console.error("Failed to fetch CTA list:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogTableChange = (logPagination) => {
    setLogPagination({
      current: logPagination.current,
      pageSize: logPagination.pageSize,
    });
    fetchLog({
      current: logPagination.current,
      pageSize: logPagination.pageSize,
    });
  }


  const handleTableChange = (pagination, tableFilters, sorter) => {
    const newSorter = {
      orderByColumn: sorter.columnKey ? mapSortField(sorter.columnKey) : '',
      isAsc: sorter.order === 'ascend' ? 'asc' : sorter.order === 'descend' ? 'desc' : '',
    };
    setPagination({
      current: pagination.current,
      pageSize: pagination.pageSize,
    });
    setSorter(newSorter);
    fetchCTAList(filters, {
      current: pagination.current,
      pageSize: pagination.pageSize
    }, newSorter);
  };

  const handleSearch = (values) => {
    const processedValues = { ...values };
    if (values.cta) {
      const ctaList = processCTAInput(values.cta);

      if (ctaList.length === 1) {
        processedValues.cta = ctaList[0];
      } else if (ctaList.length > 1) {
        processedValues.ctaList = ctaList;
        delete processedValues.cta;
      }
    }
    const newValues = addDateRange(processedValues, processedValues.dateRange, "lastQueryTime");
    delete newValues.dateRange;
    if (Object.prototype.hasOwnProperty.call(newValues, 'Trust List Status')) {
      newValues.isWhitelist = newValues['Trust List Status'];
      delete newValues['Trust List Status'];
    }
    const newPagination = { ...pagination, current: 1 };
    setFilters(newValues);
    setPagination({ ...pagination, current: 1 });
    fetchCTAList(newValues, newPagination, sorter);
  };

  const handleView = (record) => {
    setCurrentRecord(record);
    setDrawerVisible(true);
    navigate(location.pathname, {
      replace: true, state: {
        pageInfo: { current: pagination.current, pageSize: pagination.pageSize },
        filters: filters,
        sorter: sorter
      }
    });
  };

  const handleUpdateStatus = (record) => {
    setSelectedRows([record])
    setSelectedRowKeys([record.key]);
    setIsModalVisible(true);
  };
  const copyToClipboard = (text) => {

    if (navigator.clipboard) {
      navigator.clipboard.writeText(text)
        .then(() => message.success('Copyed'));
    }
  };
  const mapSortField = (columnKey) => {
    const mapping = {
      firstTime: 'firstTime',
      lastQueryTime: 'lastQueryTime',
    };
    return mapping[columnKey] || columnKey;
  };
  const handleOpenColumnSettings = () => {
    const currentConfig = savedColumnConfig.length > 0 ? savedColumnConfig : defaultColumnConfig;
    setDraftColumnConfig(cloneColumnConfigs(currentConfig));
    setIsColumnSettingsOpen(true);
  };

  const handleToggleColumn = (columnKey, visible) => {
    setDraftColumnConfig((currentConfig) =>
      currentConfig.map((column) =>
        column.key === columnKey ? { ...column, visible } : column
      )
    );
  };

  const handleToggleAllColumns = (event) => {
    const { checked } = event.target;
    setDraftColumnConfig((currentConfig) =>
      currentConfig.map((column) => ({ ...column, visible: checked }))
    );
  };

  const handleColumnDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) {
      return;
    }

    setDraftColumnConfig((currentConfig) => {
      const activeIndex = currentConfig.findIndex((column) => column.key === active.id);
      const overIndex = currentConfig.findIndex((column) => column.key === over.id);

      if (activeIndex === -1 || overIndex === -1) {
        return currentConfig;
      }

      return arrayMove(currentConfig, activeIndex, overIndex);
    });
  };

  const handleResetColumnSettings = () => {
    const resetConfig = cloneColumnConfigs(defaultColumnConfig);
    clearColumnSettings(columnSettingsStorageKey);
    setSavedColumnConfig(resetConfig);
    setDraftColumnConfig(resetConfig);
    message.success("CTA table columns have been reset.");
  };

  const handleSaveColumnSettings = () => {
    if (!draftColumnConfig.some((column) => column.visible)) {
      message.warning("Please keep at least one CTA table column visible.");
      return;
    }

    const nextConfig = isCompanyUser
      ? moveColumnsToFront(cloneColumnConfigs(draftColumnConfig), ["ctaId"])
      : cloneColumnConfigs(draftColumnConfig);
    persistColumnSettings(columnSettingsStorageKey, nextConfig);
    setSavedColumnConfig(nextConfig);
    setIsColumnSettingsOpen(false);
    message.success("CTA table column settings saved.");
  };

  const availableColumns = [
    {
      title: "CTA ID",
      dataIndex: "ctaId",
      key: "ctaId",
      render: (text, record) => (
        <Space size={2}>
          <EllipsisText text={text} width={50} />
          {isCompanyUser && <CtaTicketStatusTag record={record} />}
        </Space>
      ),
    },
    {
      title: "Ticket Status",
      key: "ticketStatus",
      width: 80,
      render: (_, record) => isCompanyUser ? <CtaTicketStatusTag record={record} /> : null,
    },
    {
      title: "Standard CTA Format",
      dataIndex: "finalCta",
      key: "finalCta",
      width: 200,
      render: (text) => <EllipsisText text={text} />,
    },
    {
      title: "Original CTA Content",
      dataIndex: "cta",
      key: "cta",
      width: 200,
      render: (text) => (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            cursor: "pointer"
          }}
        >
          <CopyOutlined onClick={() => copyToClipboard(text)} />
          <EllipsisText text={text} link={true} />
        </div>
      ),
    },
    {
      title: "Trust List Status",
      dataIndex: "isWhitelist",
      key: "isWhitelist",
      render: (text) => {
        let color = "default";
        switch (text) {
          case "5":
            text = "Blacklisted(AI)";
            color = "red";
            break;
          case "4":
            text = "Whitelisted(AI)";
            color = "green";
            break;
          case "3":
            text = "Not Listed(AI)";
            color = "#9CA3AF";
            break;
          case "2":
            text = "Blacklisted";
            color = "red";
            break;
          case "1":
            text = "Whitelisted";
            color = "green";
            break;
          case "0":
            text = "Not Listed";
            color = "#9CA3AF";
            break;
        }
        return <Tag color={color}>{text}</Tag>;
      },
    },
    {
      title: "CTA Type",
      dataIndex: "ctaType",
      key: "ctaType",
    },
    {
      title: 'Company',
      dataIndex: 'companyName',
      key: 'companyName',
      render: (companyName) => {
        if (!companyName) {
          return '-';
        }
        if (Array.isArray(companyName)) {
          if (companyName.length === 1 && companyName[0] === null) {
            return '-';
          }
          return (
            <div>
              {companyName.map((name, index) => (
                <div key={index}>{name}</div>
              ))}
            </div>
          );
        }
        return companyName;
      }
    },
    {
      title: "Related Brands",
      dataIndex: "relatedBrands",
      key: "relatedBrands",
      render: (_, record) => (
        <div>
          {(record.relatedBrands != null && record.relatedBrands !== '') && <Tag color="green">
            {record.relatedBrands}
          </Tag>}

          {(record.workOrderId != null && record.workOrderId !== '') || (record.brandName != null && record.brandName !== '') ? (
            <div style={{ marginTop: 4 }}>
              {(() => {
                const workOrders = record.workOrderId ? String(record.workOrderId).split(',').map(item => item.trim()) : [];
                const brands = record.brandName ? String(record.brandName).split(',').map(item => item.trim()) : [];
                const pairs = [];
                
                const maxLength = Math.max(workOrders.length, brands.length);
                for (let i = 0; i < maxLength; i++) {
                  pairs.push({
                    workOrder: workOrders[i] || '',
                    brand: brands[i] || ''
                  });
                }
                
                return pairs.map((pair, index) => (
                  <div key={index} style={{ marginBottom: 4, display: 'flex', alignItems: 'center', gap: 8 }}>
                    {pair.workOrder && <Tag color="blue">{pair.workOrder}</Tag>}
                    {pair.brand && <Tag color="blue">{pair.brand}</Tag>}
                  </div>
                ));
              })()}
            </div>
          ) : null}
        </div>
      ),
    },
    {
      title: "Status",
      dataIndex: "ctaStatus",
      key: "ctaStatus",
      render: (text) => {
        let color = "default";
        switch (text) {
          case "Under Review":
            color = "processing";
            break;
          case "Verified":
            color = "success";
            break;
          case "Submitted":
            color = "warning";
            break;
          case "Disruption In Progress":
            color = "gold";
            break;
          case "Verified Genuine":
            color = "default";
            break;
          case "Verified Scam":
            color = "red";
            break;
          case "Notify Brand":
            color = "cyan";
            break;
          case "Scam Disrupted":
            color = "purple";
            break;
        }
        return <Tag color={color}>{text}</Tag>;
      },
    },
    {
      title: "Report Number",
      dataIndex: "srNames",
      key: "srNames",
      width: 150,
      render: (srNames) => (
        <EllipsisText text={srNames.join(", ")} width={100} />
      ),
    },
    {
      title: "Associated Conversation Count",
      dataIndex: "conversationCount",
      key: "conversationCount",
    },
    {
      title: "Reported",
      dataIndex: "isReport",
      key: "isReport",
      render: (isReport) => {
        const reported = isReport === 1 || isReport === "1";
        return reported ? "Yes" : "No";
      },
    },
    {
      title: "Risk Level",
      dataIndex: "riskLevel",
      key: "riskLevel",
      render: (text) => {
        let color = "default";
        switch (text) {
          case "High":
            color = "red";
            break;
          case "Moderate":
            color = "orange";
            break;
          case "Low":
            color = "gold";
            break;
          case "Minimal":
            color = "green";
            break;
        }
        return <Tag color={color}>{text}</Tag>;
      },
    },
    {
      title: "First Detected Time",
      dataIndex: "firstTime",
      key: "firstTime",
      render: (firstTime) =>
        firstTime ? dayjs(firstTime).format("YYYY-MM-DD HH:mm:ss") : "-",
      sorter: true,
    },
    {
      title: "Last Updated Time",
      dataIndex: "lastQueryTime",
      key: "lastQueryTime",
      render: (lastQueryTime) =>
        lastQueryTime
          ? dayjs(lastQueryTime).format("YYYY-MM-DD HH:mm:ss")
          : "-",
      sorter: true,
    },
    {
      title: "Actions",
      key: "action",
      width: 80,
      className: styles.actionColumn,
      render: (text, record) => (
        <div style={{ width: "80px" }}>
          <Space className={styles.actionSpace} size="small">
            <Button
              type="link"
              className={styles.actionButton}
              onClick={() => handleView(record)}
            >
              View
            </Button>
            {userProfile?.user?.userStatus !== "Company User" && (
              <Button
                type="link"
                className={styles.actionButton}
                onClick={() => handleUpdateStatus(record)}
              >
                Update
              </Button>
            )}
          </Space>
        </div>
      ),
    },
  ].filter((col) => !(isCompanyUser && hiddenColumnsForCompanyUser.includes(col.dataIndex ?? col.key)));
  const activeColumnConfig = savedColumnConfig.length > 0 ? savedColumnConfig : defaultColumnConfig;
  const allColumnsChecked = draftColumnConfig.length > 0 && draftColumnConfig.every((column) => column.visible);
  const indeterminateColumnsChecked = draftColumnConfig.some((column) => column.visible) && !allColumnsChecked;
  const columnMap = new Map(availableColumns.map((column) => [column.key, column]));
  const columns = activeColumnConfig
    .filter((column) => column.visible)
    .map((column, index, visibleColumns) => {
      const matchedColumn = columnMap.get(column.key);

      if (!matchedColumn) {
        return null;
      }

      if (column.key === "action") {
        return {
          ...matchedColumn,
          fixed: index === visibleColumns.length - 1 ? "right" : undefined,
        };
      }

      return matchedColumn;
    })
    .filter(Boolean);
  const [expandedKeys, setExpandedKeys] = useState({});

  const toggleExpand = (key) => {
    setExpandedKeys(prev => ({ ...prev, [key]: !prev[key] }));
  };
  const logColumns = [
    {
      title: "CTA",
      dataIndex: ["ctaMap", "cta"],
      key: "ctaMap_cta",
      width: 300,
      render: (_, record) => {
        const raw = record.ctaMap?.cta || "-";
        if (raw === "-") return raw;

        const cleaned = raw.replace(/^\[|\]$/g, "");
        const items = cleaned.split(",").map(item => item.trim());

        const expanded = !!expandedKeys[record.operId]; // 瑜版挸澧犵悰灞炬Ц閸氾箑鐫嶅鈧?
        const displayedItems = expanded ? items : items.slice(0, 3);
        const hasMore = items.length > 3;

        return (
          <div style={{ whiteSpace: "pre-wrap", wordBreak: "break-all" }}>
            {displayedItems.map((item, index) => (
              <div key={index}>{item}</div> // 娑撳秴鍟€闂勫嫬濮為柅妤€褰?
            ))}
            {hasMore && !expanded && (
              <div
                style={{ color: "blue", cursor: "pointer" }}
                onClick={() => toggleExpand(record.operId)}
              >
                ...Expand
              </div>
            )}
            {expanded && hasMore && (
              <div
                style={{ color: "blue", cursor: "pointer" }}
                onClick={() => toggleExpand(record.operId)}
              >
                Collapse
              </div>
            )}
          </div>
        );
      }
    },
    {
      title: "Operation",
      dataIndex: ["ctaMap", "operation"],
      key: "ctaMap_operation",
      width: 200,
      render: (_, record) => record.ctaMap?.operation || "-"
    },
    {
      title: "Operator",
      dataIndex: ["ctaMap", "operName"],
      key: "ctaMap_operName",
      width: 200,
      render: (_, record) => record.ctaMap?.operName || "-"
    },
    {
      title: "Operation Time",
      dataIndex: ["ctaMap", "operTime"],
      key: "ctaMap_operTime",
      width: 200,
      render: (_, record) => {
        const operTimeStr = record.ctaMap?.operTime;
        if (!operTimeStr) return "-";
        const utcString = operTimeStr.replace(" ", "T") + "Z";
        const date = new Date(utcString);
        return date.toLocaleString();
      }
    },
    {
      title: "Result",
      dataIndex: ["ctaMap", "result"],
      key: "ctaMap_result",
      width: 200,
      render: (_, record) => {
        const result = record.ctaMap?.result || "-";
        const color = result === "Success" ? "green" :
          result === "Failure" ? "red" : "inherit";
        return <span style={{ color }}>{result}</span>;
      }
    },
  ];

  return (
    <div>
      <Form form={form} onFinish={handleSearch}>
        <Row gutter={[16, 16]} align="middle">
          <Col span={10}>
            <Form.Item name="cta" style={{ maxWidth: 500 }}>
              <TextArea
                placeholder="Enter content to search"
                rows={1}
                maxLength={2000}
                style={{
                  resize: 'vertical',
                  minHeight: '32px',
                  lineHeight: '22px'
                }}
                onInput={(e) => {
                  const textarea = e.target;
                  textarea.style.height = 'auto';
                  const scrollHeight = textarea.scrollHeight;
                  const lineHeight = 22;
                  const maxHeight = lineHeight * 8;

                  if (scrollHeight <= maxHeight) {
                    textarea.style.height = scrollHeight + 'px';
                  } else {
                    textarea.style.height = maxHeight + 'px';
                  }
                }}
              />
            </Form.Item>
          </Col>
          <Col span={10}>
            <Form.Item name="dateRange">
              <DateRangeSelector />
            </Form.Item>
          </Col>

        </Row>
        <Row gutter={[16, 16]}>
          <Col span={6}>
            <Form.Item name="levels" label="Risk (multi-select)">
              <Select
                placeholder="Risk (multi-select)"
                allowClear
                mode="multiple"
                options={[
                  { value: "High", label: "High" },
                  { value: "Moderate", label: "Moderate" },
                  { value: "Low", label: "Low" },
                  { value: "Minimal", label: "Minimal" },
                ]}
              ></Select>
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="isReport" label="Reported">
              <Select
                placeholder="Reported"
                allowClear
                options={[
                  { value: "1", label: "Yes" },
                  { value: "0", label: "No" },
                ]}
              ></Select>
            </Form.Item>
          </Col>

          <Col span={userProfile?.user?.userStatus !== "Company User" ? 6 : 8}>
            <Form.Item name="status" label="Status (multi-select)">
              <Select
                placeholder="Status (multi-select)"
                allowClear
                mode="multiple"
                options={statusOptions}
              ></Select>
            </Form.Item>
          </Col>
          {userProfile?.user?.userStatus !== "Company User" && (
            <Col span={6}>
              <Form.Item name="companyId" label="Company">
                <Select placeholder="Company" allowClear>
                  {companys.map((company) => (
                    <Option key={company.id} value={company.id}>
                      {company.companyName}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          )}
        </Row>
        <Row gutter={[16, 16]}>
          <Col span={6}>
            <Form.Item name="brands" label="Brand (multi-select)">
              <Select
                placeholder="Brand (multi-select)"
                allowClear
                mode="multiple"
                options={brands}
              ></Select>
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="Trust List Status" label="Trust List Status">
              <Select
                placeholder="Trust List Status"
                allowClear
                options={[
                  { value: 0, label: "Not Listed" },
                  { value: 1, label: "Whitelisted" },
                  { value: 2, label: "Blacklisted" },
                  { value: 3, label: "Not Listed(AI)" },
                  { value: 4, label: "Whitelisted(AI)" },
                  { value: 5, label: "Blacklisted(AI)" },
                ]}
              ></Select>
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="types" label="CTA Type">
              <Select
                placeholder="CTA Type (multi-select)"
                allowClear
                mode="multiple"
                options={[
                  { value: "URL", label: "URL" },
                  { value: "Email", label: "Email" },
                  { value: "BankAccount", label: "BankAccount" },
                  { value: "Card", label: "Card" },
                  { value: "Phone", label: "Phone" },
                  { value: "ShortCode", label: "ShortCode" },
                  { value: "other", label: "Other" },
                ]}
              ></Select>
            </Form.Item>
          </Col>
        </Row>
        <Row >
          <Col span={24}>
            <div className={styles.tableToolbar}>
              <Space wrap className={styles.tableToolbarActions}>
                <Button type="primary" htmlType="submit">
                  Search
                </Button>
                <Button onClick={resetData}>Reset</Button>
                {userProfile?.user?.userStatus !== "Company User" && (
                  <Button type="primary" onClick={handleBatchUpdate}>
                    Batch Update
                  </Button>
                )}
                {isCompanyUser && (
                  <Button
                    type="primary"
                    disabled={selectedRows.length === 0}
                    onClick={handleCreateTicket}
                    style={selectedRows.length === 0 ? {} : { backgroundColor: '#faad14', borderColor: '#faad14' }}
                  >
                    Create Ticket
                  </Button>
                )}
                <Button type="primary" onClick={handleExport}>Export</Button>
                <Button
                  size='small'
                  icon={
                    <img
                      src={logIcon}
                      alt="log"
                      style={{ width: 30, height: 30 }}
                    />
                  }
                  onClick={() => fetchLog(logPagination)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: 0,
                    border: "none",
                    background: "none",
                    boxShadow: "none",
                  }}
                />
              </Space>
              <Button
                type="text"
                icon={<SettingOutlined />}
                onClick={handleOpenColumnSettings}
                className={styles.columnSettingsTrigger}
              />
            </div>
          </Col>
        </Row>
      </Form>

      <Table
        rowSelection={{
          type: "checkbox",
          selectedRowKeys,
          onChange: (selectedRowKeys, selectedRows) => {
            setSelectedRowKeys(selectedRowKeys);
            setSelectedRows(selectedRows);
          },
        }}
        columns={columns}
        dataSource={data.rows}
        loading={loading}
        pagination={{
          total: data.total,
          current: pagination.current,
          pageSize: pagination.pageSize,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total) => `${total} in total`,
        }}
        scroll={{ x: "max-content" }}
        onChange={handleTableChange}
      />

      <Drawer
        title="CTA Table Columns"
        placement="right"
        width={360}
        open={isColumnSettingsOpen}
        onClose={() => setIsColumnSettingsOpen(false)}
      >
        <div className={styles.columnSettingsHeader}>
          <Checkbox
            checked={allColumnsChecked}
            indeterminate={indeterminateColumnsChecked}
            onChange={handleToggleAllColumns}
          >
            Select All
          </Checkbox>
          <Space>
            <Button type="link" onClick={handleResetColumnSettings}>
              Reset
            </Button>
            <Button type="primary" onClick={handleSaveColumnSettings}>
              Save
            </Button>
          </Space>
        </div>
        <DndContext modifiers={[restrictToVerticalAxis]} onDragEnd={handleColumnDragEnd}>
          <SortableContext
            items={draftColumnConfig.map((column) => column.key)}
            strategy={verticalListSortingStrategy}
          >
            <div className={styles.columnSettingsList}>
              {draftColumnConfig.map((column) => (
                <SortableColumnSettingItem
                  key={column.key}
                  column={column}
                  onToggle={handleToggleColumn}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      </Drawer>

      <StatusModal
        visible={isModalVisible}
        onOk={handleModalOk}
        onCancel={() => {
          setIsModalVisible(false);
          modalForm.resetFields();
          setSelectedRowKeys([]);
          setSelectedRows([]);
        }}
        form={modalForm}
        statusModal={2}
      />

      <Drawer
        placement="right"
        width={"80%"}
        onClose={() => setDrawerVisible(false)}
        open={drawerVisible}
      >
        {currentRecord && (
          <CTADetail
            data={currentRecord}
            pageState={{
              pageInfo: { current: pagination.current, pageSize: pagination.pageSize },
              filters: filters,
              sorter: sorter
            }}
          />
        )}
      </Drawer>
      <Drawer
        placement="right"
        width={"80%"}
        onClose={closeDrawer}
        open={isLogCenterOpen}
      >
        <Table
          rowKey="operId"
          columns={logColumns}
          dataSource={logData.rows}
          loading={loading}
          pagination={{
            total: logData.total,
            current: logPagination.current,
            pageSize: logPagination.pageSize,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `${total} in total`,
          }}
          scroll={{ x: "max-content" }}
          onChange={handleLogTableChange}
        />
      </Drawer>

      <CtaTicketCreateModal
        visible={ticketModalVisible}
        onCancel={() => {
          setTicketModalVisible(false);
          setEligibleRecords([]);
        }}
        onSuccess={handleTicketSuccess}
        eligibleRecords={eligibleRecords}
      />
    </div >
  );
};

export default CTAList;
